import { DatasheetFolderListDto } from "@common/dto/folder/datasheet-list-dto";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { DatasheetFormatType } from "@common/dto/datasheet/type";

// Re-export for convenience
export { DatasheetFormatType };

export enum DatasheetFormatManagementMode {
  export = "export",
  load = "load",
  new = "new",
  import = "import",
}

export type DatasheetFormatManagementSubmitExportParams = {
  format: { id: number; name: string };
  folder: FolderPrimaryListDto;
  context: number;
  tds: boolean;
};

export type DatasheetFormatManagementSubmitImportParams = {
  folder: FolderPrimaryListDto;
};

export type DatasheetFormatManagementSubmitLoadParams = {
  format: { id: number; name: string };
  folder: FolderPrimaryListDto;
  context: number;
  tableContext?: number;
  tableFieldId?: number | null;
};

export type DatasheetFormatManagementSubmitNewParams = {};

export type DatasheetFormatManagementSubmitParams =
  | DatasheetFormatManagementSubmitExportParams
  | DatasheetFormatManagementSubmitImportParams
  | DatasheetFormatManagementSubmitLoadParams
  | DatasheetFormatManagementSubmitNewParams;

export type DatasheetFormatManagementProps = {
  onClose: () => void;
  /**
   * Optional initial selections when opening the modal.
   * Used by IDS builder to open "Add New Datasheet" pre-configured as a Table Datasheet.
   */
  initialSelectedFormatType?: DatasheetFormatType;
  initialTableField?: { id: number; name: string; context: number };
  getId: {
    title: string;
    folderChooserBtn?: string;
    selectedFolderNameHolder?: string;
    formatTypes: string;
    formatName?: string;
  };
  getFormats: (folderId: number) => Promise<Array<DatasheetFormatManagementFormat>>;
  onSubmit: (params: DatasheetFormatManagementSubmitParams) => void;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  checkFormatNameExists: () => Promise<boolean>;
  isAllowedToSubmit?: (params: {
    folderId?: FolderPrimaryListDto["id"];
    formatId?: DatasheetFormatManagementFormat["id"];
    newFormatName?: string;
  }) => boolean;
  mode: DatasheetFormatManagementMode;
  isAllowedToSubmitFormatManagementSubmit?: (params: {
    folderId?: FolderPrimaryListDto["id"];
    formatId?: DatasheetFormatManagementFormat["id"];
    newFormatName?: string;
  }) => boolean;
  isExporting?: boolean;
  importContext?: number;
  importFormatName?: string;
  /**
   * Optional table field id derived from the imported XML (for TDS imports).
   * When present and non-zero, import logic can fetch/check formats for that
   * specific table field via TDS APIs instead of generic DS APIs.
   */
  importTableFieldId?: number;
};

export type DatasheetFormatManagementFormat = {
  id: number;
  name: string;
};

export type DatasheetFormatManagementConfig = {
  title: string;
  submitBtnText: string;
  isAllowedToSubmit: (params: {
    folderId?: FolderPrimaryListDto["id"];
    formatId?: DatasheetFormatManagementFormat["id"];
    newFormatName?: string;
  }) => boolean;
  showFormatName?: boolean;
  formatManagement: {
    title: string;
    isAllowedSubmit: (params: {
      folderId?: FolderPrimaryListDto["id"];
      formatId?: DatasheetFormatManagementFormat["id"];
      newFormatName?: string;
    }) => boolean;
    submitBtnProps: {
      text: string;
    };
    deleteFormatBtnProps?: {
      text: string;
    };
    handleFormatDelete?: (folderId: number, formatId: number) => Promise<void>;
    handleFolderDelete?: (folderId: number) => Promise<void>;
    handleFolderCreate?: (data: { parentId?: number; name: string }) => Promise<void>;
    handleFormatRename?: (folderId: number, formatId: number, currentName: string) => void;
  };
};

export type GetTdsFolderFormats = {
  folderId: DatasheetFolderListDto['folderId'];
  context: DatasheetFolderListDto['fieldContext'];
  tableId: number;
};

export type GetTableFieldSubfieldsParams = {
  fieldId: number;
  context: number;
};